import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface IncomeCalculatorProps {
  isOpen: boolean;
  onClose: () => void;
}
export function IncomeCalculator({
  isOpen,
  onClose
}: IncomeCalculatorProps) {
  const [contacts, setContacts] = useState(10);
  const [closeRate, setCloseRate] = useState(30);
  const [price, setPrice] = useState(350);
  const [recurring, setRecurring] = useState(false);
  const [bump, setBump] = useState(false);
  // Calculate results
  const closed = Math.round(contacts * (closeRate / 100));
  const sitesIncome = closed * price;
  const bumpIncome = bump ? closed * 17 : 0;
  const weeklyIncome = sitesIncome + bumpIncome;
  const monthlyIncome = weeklyIncome * 4;
  const recurringMonth = recurring ? closed * 4 * 99 : 0;
  const yearlyIncome = monthlyIncome * 12 + recurringMonth * 12;
  const fmt = (n: number) => {
    return '$' + Math.round(n).toLocaleString('en-US');
  };
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-328">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]" data-id="element-329" />

          {/* Modal */}
          <div className="fixed inset-0 z-[101] overflow-y-auto" data-id="element-330">
            <div className="min-h-full flex items-center justify-center p-4" data-id="element-331">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} transition={{
            duration: 0.3
          }} className="relative w-full max-w-4xl bg-navy border border-gold/30 rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-332">
                {/* Close button */}
                <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 rounded-lg bg-navy-dark/50 hover:bg-navy-dark border border-gold/20 hover:border-gold/40 transition-colors" aria-label="Cerrar calculadora" data-id="element-333">
                  <X className="h-5 w-5 text-gray-300" data-id="element-334" />
                </button>

                {/* Content */}
                <div className="p-6 md:p-8 max-h-[85vh] overflow-y-auto" data-id="element-335">
                  {/* Header */}
                  <div className="text-center mb-8" data-id="element-336">
                    <h3 className="font-heading font-black text-3xl md:text-4xl mb-2" data-id="element-337">
                      ¿Cuánto Puedes Ganar{' '}
                      <span className="text-gold" data-id="element-338">Esta Semana?</span>
                    </h3>
                    <p className="font-body text-sm text-gray-400" data-id="element-339">
                      Mueve los controles y calcula tu ingreso real con el
                      sistema AI Web Closer
                    </p>
                  </div>

                  {/* Calculator Card */}
                  <div className="bg-navy-dark border border-gold/20 rounded-xl p-6 md:p-8 mb-6" data-id="element-340">
                    <div className="font-heading font-bold text-xs tracking-widest text-gold uppercase mb-6" data-id="element-341">
                      Calculadora de Ingresos
                    </div>

                    {/* Slider 1: Contacts */}
                    <div className="mb-6" data-id="element-342">
                      <div className="flex justify-between items-center mb-2" data-id="element-343">
                        <span className="font-body text-sm text-gray-300" data-id="element-344">
                          Negocios que contactas por semana
                        </span>
                        <div className="font-heading font-bold text-lg text-gold bg-gold/10 border border-gold/30 rounded-lg px-3 py-1 min-w-[60px] text-center" data-id="element-345">
                          {contacts}
                        </div>
                      </div>
                      <input type="range" min="1" max="50" value={contacts} onChange={e => setContacts(Number(e.target.value))} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-gold" data-id="element-346" />
                    </div>

                    {/* Slider 2: Close Rate */}
                    <div className="mb-6" data-id="element-347">
                      <div className="flex justify-between items-center mb-2" data-id="element-348">
                        <span className="font-body text-sm text-gray-300" data-id="element-349">
                          Tasa de cierre (% que dice SÍ)
                        </span>
                        <div className="font-heading font-bold text-lg text-gold bg-gold/10 border border-gold/30 rounded-lg px-3 py-1 min-w-[60px] text-center" data-id="element-350">
                          {closeRate}
                          <span className="text-xs" data-id="element-351">%</span>
                        </div>
                      </div>
                      <input type="range" min="5" max="60" value={closeRate} onChange={e => setCloseRate(Number(e.target.value))} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-gold" data-id="element-352" />
                    </div>

                    {/* Slider 3: Price */}
                    <div className="mb-8" data-id="element-353">
                      <div className="flex justify-between items-center mb-2" data-id="element-354">
                        <span className="font-body text-sm text-gray-300" data-id="element-355">
                          Precio por sitio web
                        </span>
                        <div className="font-heading font-bold text-lg text-gold bg-gold/10 border border-gold/30 rounded-lg px-3 py-1 min-w-[70px] text-center" data-id="element-356">
                          ${price}
                        </div>
                      </div>
                      <input type="range" min="150" max="1500" step="50" value={price} onChange={e => setPrice(Number(e.target.value))} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-gold" data-id="element-357" />
                    </div>

                    {/* Divider */}
                    <div className="relative mb-6" data-id="element-358">
                      <div className="absolute inset-0 flex items-center" data-id="element-359">
                        <div className="w-full border-t border-gold/20" data-id="element-360"></div>
                      </div>
                    </div>

                    {/* Toggle: Recurring */}
                    <div className="flex items-center gap-3 mb-4 p-3 bg-navy-light/50 rounded-lg border border-gold/10" data-id="element-361">
                      <span className="font-body text-sm text-gray-300 flex-1" data-id="element-362">
                        Añadir paquete de mantenimiento mensual ($99/cliente)
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-363">
                        <input type="checkbox" checked={recurring} onChange={e => setRecurring(e.target.checked)} className="sr-only peer" data-id="element-364" />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold" data-id="element-365"></div>
                      </label>
                      <div className="font-heading font-bold text-xs text-navy bg-gold rounded px-2 py-1" data-id="element-366">
                        +$99/mes
                      </div>
                    </div>

                    {/* Toggle: Bump */}
                    <div className="flex items-center gap-3 mb-8 p-3 bg-navy-light/50 rounded-lg border border-gold/10" data-id="element-367">
                      <span className="font-body text-sm text-gray-300 flex-1" data-id="element-368">
                        Incluir upsell Kit del Primer Cliente ($17)
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-369">
                        <input type="checkbox" checked={bump} onChange={e => setBump(e.target.checked)} className="sr-only peer" data-id="element-370" />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gold" data-id="element-371"></div>
                      </label>
                      <div className="font-heading font-bold text-xs text-navy bg-gold rounded px-2 py-1" data-id="element-372">
                        +$17
                      </div>
                    </div>

                    {/* Breakdown */}
                    <div className="pt-2" data-id="element-373">
                      <div className="font-heading text-xs font-bold tracking-widest text-gray-400 uppercase mb-4" data-id="element-374">
                        Desglose Semanal
                      </div>
                      <div className="space-y-2" data-id="element-375">
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50" data-id="element-376">
                          <span className="font-body text-sm text-gray-400" data-id="element-377">
                            Negocios contactados
                          </span>
                          <span className="font-heading font-bold text-white" data-id="element-378">
                            {contacts}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50" data-id="element-379">
                          <span className="font-body text-sm text-gray-400" data-id="element-380">
                            Clientes cerrados
                          </span>
                          <span className="font-heading font-bold text-white" data-id="element-381">
                            {closed}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50" data-id="element-382">
                          <span className="font-body text-sm text-gray-400" data-id="element-383">
                            Ingreso por sitios web
                          </span>
                          <span className="font-heading font-bold text-gold" data-id="element-384">
                            {fmt(sitesIncome)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50" data-id="element-385">
                          <span className="font-body text-sm text-gray-400" data-id="element-386">
                            Ingreso recurrente (mes 1)
                          </span>
                          <span className="font-heading font-bold text-gold" data-id="element-387">
                            {recurring ? `${fmt(closed * 99)}/sem` : '$0'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-t border-gold/20 mt-2" data-id="element-388">
                          <span className="font-heading font-bold text-white" data-id="element-389">
                            Total semana 1
                          </span>
                          <span className="font-heading font-black text-xl text-gold" data-id="element-390">
                            {fmt(weeklyIncome)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Reference */}
                  <div className="bg-navy-dark border border-gold/20 rounded-xl p-6 md:p-8 mb-6" data-id="element-391">
                    <div className="font-heading font-bold text-xs tracking-widest text-gold uppercase mb-6" data-id="element-392">
                      💰 Precios de Referencia por Nicho
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-393">
                      <div className="bg-navy-light/50 border border-gray-700 rounded-lg p-4 text-center" data-id="element-394">
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-395">
                          🍕 Restaurantes
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-396">
                          $300–$500
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-397">
                          Alta urgencia
                        </div>
                      </div>
                      <div className="bg-gold/10 border-2 border-gold rounded-lg p-4 text-center relative" data-id="element-398">
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gold text-navy text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-399">
                          ⭐ RECOMENDADO
                        </div>
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-400">
                          🦷 Dentistas
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-401">
                          $500–$900
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-402">
                          Mayor ticket
                        </div>
                      </div>
                      <div className="bg-navy-light/50 border border-gray-700 rounded-lg p-4 text-center" data-id="element-403">
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-404">
                          🔧 Talleres
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-405">
                          $250–$450
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-406">
                          Fácil de cerrar
                        </div>
                      </div>
                      <div className="bg-gold/10 border-2 border-gold rounded-lg p-4 text-center relative" data-id="element-407">
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gold text-navy text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-408">
                          ⭐ RECOMENDADO
                        </div>
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-409">
                          ⚖️ Abogados
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-410">
                          $700–$1,500
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-411">
                          Máximo ticket
                        </div>
                      </div>
                      <div className="bg-navy-light/50 border border-gray-700 rounded-lg p-4 text-center" data-id="element-412">
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-413">
                          ✂️ Peluquerías
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-414">
                          $200–$350
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-415">
                          Volumen alto
                        </div>
                      </div>
                      <div className="bg-navy-light/50 border border-gray-700 rounded-lg p-4 text-center" data-id="element-416">
                        <div className="font-body text-sm font-semibold text-gray-300 mb-2" data-id="element-417">
                          🏋️ Gimnasios
                        </div>
                        <div className="font-heading font-black text-lg text-gold" data-id="element-418">
                          $350–$600
                        </div>
                        <div className="font-body text-xs text-gray-500 mt-1" data-id="element-419">
                          Buen margen
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </>}
    </AnimatePresence>;
}